import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';

/** One PaymentTermsProvider code and the credit days it resolves to (#4784). */
export interface PaymentTermCreditDays {
  code: string;
  creditDays: number;
}

/**
 * #4784 — read-only view of the API's payment-term → credit-days mapping. The rule itself lives in
 * PaymentTermsHelper server-side; this only lets the UI look up a term the user just picked so the credit
 * days and due date are right immediately, instead of only after a save round-trip.
 */
@Injectable({ providedIn: 'root' })
export class PaymentTermsProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/payment-terms';

  /**
   * The whole catalog as a code → credit-days map, fetched once and shared. It is small, static master
   * data, so caching it avoids a request on every provider / source-document selection.
   */
  private readonly creditDaysMap$: Observable<Map<string, number>> = this.httpClient
    .get<ApiResponse<PaymentTermCreditDays[]>>(`${this.baseUrl}/credit-days`)
    .pipe(
      map((response) => new Map((response.data ?? []).map((entry) => [entry.code, entry.creditDays]))),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

  public getCreditDaysMap(): Observable<Map<string, number>> {
    return this.creditDaysMap$;
  }
}
